import { createSelector } from 'reselect';
import moment from 'moment';

import makeSelectMain, { getMainClientInfo, getProfileCurrentSetting } from '../Main/selectors';
import { formatClaimDetails, getClaimEvents } from './helpers';
import { initialState } from './reducer';
import {
  CLAIMS_DEPT_AUTHOR,
  FETCH_ERROR_MESSAGE,
  MESSAGE_TYPE_CLAIM,
} from './constants';
import {
  LOSS_DRAFT_PENDING_ISSUE,
  NEW_LOSS_DRAFT_MESSAGE_PROPS,
  RESOLVED_STATUS,
} from '../Notifications/constants';
import { createNotification, createUnreadMessageNotifications } from '../Notifications/helpers';
import { formatDate, formatErrorMessages } from '../../utils/globalHelpers';

const selectLossDraftDomain = (state) => state.lossDraft || initialState;

const selectAttachment = (prop) => createSelector(
  selectLossDraftDomain,
  ({ attachment }) => attachment[prop],
);

const selectClaimByClaimNumber = () => createSelector(
  makeSelectLossDraft('claims'),
  makeSelectLossDraft('selectedClaimNumber'),
  (claims, selectedClaim) => {
    if (!selectedClaim || !claims.length) {
      return {};
    }
    const claim = claims.find(({ claimNumber }) => claimNumber === selectedClaim);
    return formatClaimDetails(claim);
  },
);

const selectClaimEventsAndMessages = () => createSelector(
  makeSelectLossDraft('messages'),
  selectClaimEventsRowData(),
  (messages, claims) => {
    const sortedMessages = [...messages, ...claims]
      .sort((a, b) => new Date(b.updated) - new Date(a.updated));
    return sortedMessages;
  },
);

const selectClaimEventsRowData = () => createSelector(
  selectLossDraftDomain,
  getProfileCurrentSetting('lastLogin'),
  ({ claimNumbersRead, claims }, { lastLoginDate }) => {
    const formattedClaimsMessages = claims.reduce((acc, claim) => {
      const {
        activeDisbursements,
        claimNumber,
        completedDisbursements,
        lossDate,
        // TODO: uncomment when inspections are added to the claims inbox/detail pages
        // inspections,
      } = claim;
      const claimEvents = getClaimEvents({
        activeDisbursements,
        completedDisbursements,
        lastLoginDate,
        lossDate,
        // TODO: uncomment when inspections are added to the claims inbox/detail pages
        // inspections,
      });
      const claimRows = claimEvents.map(({
        date,
        isUnreadClaimEvent,
        number,
        title,
      }) => ({
        author: CLAIMS_DEPT_AUTHOR,
        claimNumber,
        isUnreadClaimEvent: !claimNumbersRead.includes(claimNumber) && isUnreadClaimEvent,
        messageId: claimNumber,
        messageType: MESSAGE_TYPE_CLAIM,
        number,
        subject: title,
        updated: date,
      }));
      return [...acc, ...claimRows];
    }, []);
    return formattedClaimsMessages;
  },
);

const selectConfirmationNumber = () => createSelector(
  selectLossDraftDomain,
  ({ confirmationNumber }) => confirmationNumber,
);

const selectErrors = (prop) => createSelector(
  selectLossDraftDomain,
  ({ errors }) => errors[prop],
);

const selectConfirmationData = (prop) => createSelector(
  selectLossDraftDomain,
  ({ confirmation }) => {
    const confirmationId = confirmation[prop];

    return ([{
      label: 'Confirmation Number',
      value: confirmationId,
    }]);
  },
);

const selectDropdownClaimNumberItems = () => createSelector(
  makeSelectLossDraft('claims'),
  (claims) => (
    claims.map(({ claimNumber, claimTitle }) => (
      {
        display: `${claimNumber} (${claimTitle})`,
        value: claimNumber,
      }
    ))
  ),
);

const selectDropdownDefaultClaimNumber = () => createSelector(
  selectLossDraftDomain,
  ({ claims, selectedClaimNumber }) => {
    if (!selectedClaimNumber) {
      return claims.length ? claims[0].claimNumber : '';
    }
    return selectedClaimNumber;
  },
);

const selectForm = (prop) => createSelector(
  selectLossDraftDomain,
  ({ forms }) => forms[prop],
);

const selectFormErrors = (prop) => createSelector(
  selectLossDraftDomain,
  ({ formErrors }) => formErrors[prop],
);

const selectHasLossDraftClaims = () => createSelector(
  makeSelectLossDraft('claims'),
  (claims) => !!claims.length,
);

const selectIsLossDraftAvailable = () => createSelector(
  selectLossDraftDomain,
  makeSelectMain('isChangeSelectedLoanInProgress'),
  ({ claims }, isChangeSelectedLoanInProgress) => (
    !!claims.length || isChangeSelectedLoanInProgress
  ),
);

const selectHasUnreadLossDraftClaimEvents = () => createSelector(
  makeSelectLossDraft('claims'),
  getProfileCurrentSetting('lastLogin'),
  (claims, { lastLoginDate }) => claims.some((
    { activeDisbursements, completedDisbursements, lossDate },
  ) => (lossDate && moment(lossDate).isAfter(lastLoginDate)) ||
      [...activeDisbursements, ...completedDisbursements]
        .some(({ isUnreadClaimEvent }) => isUnreadClaimEvent)),
);

const selectHasUnreadLossDraftMessages = () => createSelector(
  selectMessages(),
  (messages) => messages.some(({
    lastOpened,
    unreadReplyCount,
  }) => unreadReplyCount > 0 || !lastOpened),
);

const selectIsMessageCached = () => createSelector(
  selectLossDraftDomain,
  ({ messageDetails, selectedMessageId }) => !!messageDetails[selectedMessageId],
);

const selectIsMessageStatusClosed = () => createSelector(
  selectLossDraftDomain,
  ({ messageDetails, selectedMessageId }) =>
    !!messageDetails[selectedMessageId] &&
    messageDetails[selectedMessageId].status === 'Resolved',
);

const selectLossDraftUnreadMessageNotifications = () => createSelector(
  selectMessages(),
  (messages) => createUnreadMessageNotifications({
    unreadMessages: messages.filter(({
      lastOpened,
      unreadReplyCount,
    }) => unreadReplyCount > 0 || !lastOpened)
      .map(({
        created,
        lastOpened,
        unreadReplyDates,
      }) => (!lastOpened ?
        { unreadReplyDates: unreadReplyDates ? [...unreadReplyDates, created] : [created] } :
        { unreadReplyDates })),
    ...NEW_LOSS_DRAFT_MESSAGE_PROPS,
  }),
);

const selectLossDraftMessageInquiryNotifications = () => createSelector(
  makeSelectLossDraft('messages'),
  getProfileCurrentSetting(),
  (messages, { username }) => messages
    .filter(({ author, status }) => status !== RESOLVED_STATUS && author === username)
    .map(({ created }) => createNotification({
      date: formatDate(created, null, 'MMMM D, YYYY'),
      /**
       * Using 'created' timestamp for lastUpdated so notification remains in same
       * initial sorting position after message is updated, e.g. if reply is added.
       */
      lastUpdated: created,
      name: LOSS_DRAFT_PENDING_ISSUE.name,
      text: LOSS_DRAFT_PENDING_ISSUE.text,
    })),
);

const selectLossDraftScreenReaderNotifications = () => createSelector(
  makeSelectLossDraft('error'),
  makeSelectLossDraft('errors'),
  makeSelectLossDraft('formErrors'),
  (
    error,
    {
      bannerError,
      serviceError: {
        fetchMessageDetails,
        fetchMessages,
        postMessage,
        postReply,
      },
    },
    {
      attachments,
      message,
      reply,
      uploadDocument,
    },
  ) => {
    const nonFormErrors = [
      error,
      bannerError,
      fetchMessageDetails,
      fetchMessages,
      postMessage,
      postReply,
    ];

    const notifications = nonFormErrors.filter((err) => !!err);

    notifications.concat(formatErrorMessages({
      attachments,
      ...message,
      ...reply,
      ...uploadDocument,
    }));

    return notifications;
  },
);

const selectMessageIdByClaimNumber = (claimNumber) => createSelector(
  selectMessages(),
  (messages) => {
    const claimMessage = messages.find((message) => message.claimNumber === claimNumber);
    if (claimMessage) {
      return claimMessage.messageId;
    }
    return '';
  },
);

const selectMessages = () => createSelector(
  selectLossDraftDomain,
  ({ messages }) => messages,
);

const selectOpenLossDraftClaims = () => createSelector(
  selectLossDraftDomain,
  ({ claims }) => claims.filter(({ claimStatus }) => claimStatus !== 'Close'),
);

const selectRequestStatus = (prop) => createSelector(
  selectLossDraftDomain,
  ({ requestStatus }) => requestStatus[prop],
);

const selectSelectedMessageDetails = () => createSelector(
  selectLossDraftDomain,
  selectIsMessageStatusClosed(),
  ({ messageDetails, selectedMessageId }, isMessageStatusClosed) => {
    if (messageDetails[selectedMessageId]) {
      const message = messageDetails[selectedMessageId];
      const { claimNumber } = message;
      return {
        ...message,
        isMessageStatusClosed,
        subtitle: `Claim #: ${claimNumber}`,
      };
    }
    return {
      attachments: {},
      messages: [],
    };
  },
);

const selectServiceError = (prop) => createSelector(
  makeSelectLossDraft('errors'),
  ({ serviceError }) => serviceError[prop],
);

/**
 * @description formats error message with correct phone number
 */
const selectServiceErrorMessage = () => createSelector(
  getMainClientInfo('customerServicePhoneNumber'),
  (customerServicePhoneNumber) => `${FETCH_ERROR_MESSAGE} ${customerServicePhoneNumber}.`,
);

const selectStep = () => createSelector(
  selectLossDraftDomain,
  ({ step }) => step,
);

/**
 * Default selector used by LossDraft
 */
const makeSelectLossDraft = (prop) => createSelector(
  selectLossDraftDomain,
  (state) => state[prop],
);

export default makeSelectLossDraft;
export {
  selectAttachment,
  selectClaimByClaimNumber,
  selectClaimEventsAndMessages,
  selectConfirmationData,
  selectConfirmationNumber,
  selectDropdownClaimNumberItems,
  selectDropdownDefaultClaimNumber,
  selectErrors,
  selectForm,
  selectFormErrors,
  selectHasLossDraftClaims,
  selectHasUnreadLossDraftClaimEvents,
  selectHasUnreadLossDraftMessages,
  selectIsLossDraftAvailable,
  selectIsMessageCached,
  selectIsMessageStatusClosed,
  selectLossDraftDomain,
  selectLossDraftUnreadMessageNotifications,
  selectLossDraftMessageInquiryNotifications,
  selectLossDraftScreenReaderNotifications,
  selectMessageIdByClaimNumber,
  selectMessages,
  selectOpenLossDraftClaims,
  selectRequestStatus,
  selectSelectedMessageDetails,
  selectServiceErrorMessage,
  selectServiceError,
  selectStep,
};
